import {
	Component,
	Inject,
	OnInit,
	EventEmitter,
	Output,
	Renderer2,
} from '@angular/core';

import {
	MAT_DIALOG_DATA,
	MatDialogRef,
	MatDialogModule,
} from '@angular/material/dialog';
import { event } from 'cypress/types/jquery';
import {
	ImageCroppedEvent,
	base64ToFile,
	ImageCropperModule,
} from 'ngx-image-cropper';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatButtonModule } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';

@Component({
	selector: 'app-add-background-dialog',
	templateUrl: './add-background-dialog.component.html',
	styleUrls: ['./add-background-dialog.component.scss'],
	standalone: true,
	imports: [
		NgClass,
		ExtendedModule,
		MatButtonModule,
		MatDialogModule,
		FlexModule,
		ImageCropperModule,
	],
})
export class AddBackgroundDialogComponent implements OnInit {
	@Output() valueSelected = new EventEmitter<any>();
	imageChangedEvent: any = '';
	croppedImage: any = '';
	fileUploaded = false;
	imagePath: any;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: { imagePath: any },
		public dialogRef: MatDialogRef<AddBackgroundDialogComponent>,
		public notificationService: NotificationService
	) {}

	ngOnInit(): void {
		this.imagePath = this.data?.imagePath;
	}

	fileChangeEvent(event: any): void {
		if (event?.target?.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			const allowedTypes = [
				'image/jpg',
				'image/jpeg',
				'image/png',
				'image/webp',
			];

			if (file.size < 5242880 && allowedTypes.includes(file.type)) {
				const reader = new FileReader();

				reader.onload = (e: any) => {
					const base64 = e.target.result;
					const img = new Image();
					img.src = base64;

					img.onload = () => {
						const aspectRatio = img.width / img.height;
						const canvasAspect = 2 / 1;

						if (aspectRatio < canvasAspect) {
							this.applyBlurBackground(img); // 👈 Apply blur
						} else {
							this.imageChangedEvent = event; // 👈 Normal crop flow
							this.fileUploaded = true;
						}
					};
				};

				reader.readAsDataURL(file);
			} else {
				this.notificationService.openNotification(
					$localize`:@@add_background_fileError_notification:The file you have selected is not jpg or png. Please choose a different file.`,
					'OK',
					'error'
				);
			}
		}
	}

	applyBlurBackground(img: HTMLImageElement): void {
		const width = 1600;
		const height = 800;

		// Step 1: Create SVG filter with Gaussian blur
		const svg = `
		<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
			<filter id="blurFilter">
				<feGaussianBlur in="SourceGraphic" stdDeviation="40" />
			</filter>
			<image x="0" y="0" width="${width}" height="${height}" 
				href="${img.src}" filter="url(#blurFilter)" preserveAspectRatio="xMidYMid slice" />
		</svg>`;
		const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
		const svgUrl = URL.createObjectURL(svgBlob);

		// Step 2: Load blurred image from SVG
		const blurredImg = new Image();
		const originalImg = new Image();
		blurredImg.crossOrigin = 'anonymous';
		originalImg.crossOrigin = 'anonymous';

		blurredImg.onload = () => {
			originalImg.onload = () => {
				const canvas = document.createElement('canvas');
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext('2d');
				if (!ctx) return;

				// Draw blurred background
				ctx.clearRect(0, 0, width, height);
				ctx.drawImage(blurredImg, 0, 0, width, height);

				// Draw original sharp image centered on top
				const ratio = Math.min(
					width / originalImg.width,
					height / originalImg.height
				);
				const newWidth = originalImg.width * ratio;
				const newHeight = originalImg.height * ratio;
				const offsetX = (width - newWidth) / 2;
				const offsetY = (height - newHeight) / 2;

				ctx.drawImage(originalImg, offsetX, offsetY, newWidth, newHeight);

				// Export final canvas
				canvas.toBlob((blob) => {
					if (!blob) return;

					const newFile = new File([blob], 'modified.png', {
						type: 'image/png',
					});

					const fakeEvent: any = {
						target: {
							files: [newFile],
						},
					};

					this.imageChangedEvent = fakeEvent;
					this.fileUploaded = true;

					// Cleanup
					URL.revokeObjectURL(svgUrl);
				}, 'image/png');
			};

			// Load original after blurred
			originalImg.src = img.src;
		};

		blurredImg.src = svgUrl;
	}

	imageCropped(event: ImageCroppedEvent) {
		this.croppedImage = event.base64;
	}

	imageLoaded() {
		// Show cropper if needed
	}

	cropperReady() {
		// Cropper ready if needed
	}

	loadImageFailed() {}

	onCancelClick(): void {
		this.dialogRef.close();
	}

	dialogClose() {
		this.valueSelected.emit(this.croppedImage);
		this.dialogRef.close();
	}
}
